import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth, useTellescopeMutation } from "@tellescope/tanstack-query";
import { useLocalTracks } from "@tellescope/video-calling";
import { toast } from "sonner";
import { useVideoCallingStore } from "../../store";
import { useCallingShallowStore } from "../../store/calling-store";
import { Enduser } from "../EnduserSearchCombobox";
import { getISO6391Code } from "@tellescope/utils";

const VIDEO_CALL_DURATION_MINUTES = 30;

/**
 * Logic for the ad-hoc video call dialer flow.
 *
 * Creates a transient "Video Call" calendar event (Twilio video enabled) attended by the
 * current user plus the selected endusers, sends invite notifications (SMS when opted in,
 * email whenever the enduser has one), then opens the video call using the same flow as
 * starting a call from an existing calendar event.
 */
export const useVideoCallDialer = () => {
  const { t } = useTranslation("calling");
  const { currentUser } = useAuth();
  const videoCalling = useVideoCallingStore();
  const { createLocalTracks } = useLocalTracks();
  const { closeDialer } = useCallingShallowStore("closeDialer");

  const [selectedEndusers, setSelectedEndusers] = useState<Enduser[]>([]);
  const [sendSMS, setSendSMS] = useState(true);

  const createEvent = useTellescopeMutation({
    model: "calendar_events",
    method: "createOne",
    onError: () => {
      toast.error(t("Error starting video call. Please try again."));
    },
  });
  const createSMS = useTellescopeMutation({
    model: "sms_messages",
    method: "createOne",
    onError: () => {
      toast.error(t("Error sending SMS invite."));
    },
  });
  const createEmail = useTellescopeMutation({
    model: "emails",
    method: "createOne",
    onError: () => {
      toast.error(t("Error sending email invite."));
    },
  });

  const onStartVideoCall = useCallback(() => {
    if (!currentUser || selectedEndusers.length === 0) return;

    createEvent.mutate(
      {
        title: "Video Call",
        enableVideoCall: true,
        videoIntegration: "Twilio",
        startTimeInMS: Date.now(),
        durationInMinutes: VIDEO_CALL_DURATION_MINUTES,
        attendees: [
          { type: "user", id: currentUser.id },
          ...selectedEndusers.map<{ type: "user" | "enduser"; id: string }>((enduser) => ({
            type: "enduser",
            id: enduser.id,
          })),
        ],
      },
      {
        onSuccess: (event) => {
          videoCalling.setMeetingMeta({
            calendarEventId: event.id,
            eventTitle: event.title,
            isHost: true,
          });
          videoCalling.setIsWaitingRoom(true);
          videoCalling.setIsVisible(true);
          createLocalTracks().catch(() => {
            // Error is handled and stored in state by createLocalTracks
          });

          closeDialer();

          const journeyContext = { calendarEventId: event.id };

          selectedEndusers.forEach((enduser) => {
            const lng = getISO6391Code(enduser.language?.iso6391 || enduser.language?.displayName);
            const message =
              t("Join your video call here:", {
                lng,
              }) + ` {{calendar_event.portal_link:${t("Join Call Here", { lng })}}}`;
            if (sendSMS && enduser.phone) {
              createSMS.mutate({
                enduserId: enduser.id,
                enduserPhoneNumber: enduser.phone,
                message,
                journeyContext,
                autoResolveToFrom: true,
              });
            }
            if (enduser.email) {
              createEmail.mutate({
                enduserId: enduser.id,
                subject: t("Video Call"),
                textContent: message,
                journeyContext,
              });
            }
          });
        },
      }
    );
  }, [
    currentUser,
    selectedEndusers,
    createEvent,
    videoCalling,
    createLocalTracks,
    closeDialer,
    t,
    sendSMS,
    createSMS,
    createEmail,
  ]);

  return {
    selectedEndusers,
    setSelectedEndusers,
    sendSMS,
    setSendSMS,
    onStartVideoCall,
    isSubmitting: createEvent.isPending,
    canStart: selectedEndusers.length > 0 && !createEvent.isPending,
  };
};
